/**
 * Branch switcher (top bar), the permanent "you are in a branch" banner, the
 * change-request dialog, and the protected-main explanation.
 *
 * The routing itself lives in lib/branchSession: once a branch is active,
 * every platform request goes to the branch tenant. These components only
 * choose, create and leave branches, and open the change request to main.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, GitBranch, GitPullRequest, Lock, Plus, ShieldAlert, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { Button } from '../design-system/components/Button';
import { useReleaseStore, type Branch, type ForkMode } from '../store/releaseStore';
import { getTenantId } from '../store/authStore';
import { useNavigationStore } from '../store/navigationStore';
import { useUiV2 } from './uiFlags';
import {
  PROTECTED_MAIN_EVENT, switchBranch, useActiveBranch, type ActiveBranch,
} from '../lib/branchSession';
import './branchBar.css';

const FORK_POLL_MS = 1500;
const FORK_WAIT_MS = 120_000;
const LIVE = new Set(['active', 'in_review']);

/** Which dialog is open. Shared by the bar, the banner and the menu. */
interface BranchUi {
  requestOpen: boolean;
  protectedMessage: string | null;
  setRequestOpen: (open: boolean) => void;
  setProtectedMessage: (message: string | null) => void;
}
export const useBranchUi = create<BranchUi>((set) => ({
  requestOpen: false,
  protectedMessage: null,
  setRequestOpen: (requestOpen) => set({ requestOpen }),
  setProtectedMessage: (protectedMessage) => set({ protectedMessage }),
}));

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function toActive(b: Pick<Branch, 'name' | 'tenant_id'>): ActiveBranch {
  return { name: b.name, tenant_id: b.tenant_id, main_tenant_id: getTenantId() };
}

/** Create a branch and wait for its fork to land; resolves with the live branch. */
async function createAndWait(name: string, mode: ForkMode): Promise<Branch> {
  const store = useReleaseStore.getState();
  const created = await store.createBranch(name, { mode, object_types: [], events_days: 90 });
  const wanted = created.branch?.name ?? name;
  const deadline = Date.now() + FORK_WAIT_MS;
  for (;;) {
    const b = useReleaseStore.getState().branches.find((x) => x.name === wanted);
    if (b?.status === 'active') return b;
    if (b?.status === 'failed') throw new Error(b.fork_report?.error || 'No se pudo crear la rama.');
    if (Date.now() > deadline) throw new Error('La rama sigue creándose. Elígela en el selector en unos segundos.');
    await sleep(FORK_POLL_MS);
    await useReleaseStore.getState().fetchBranches();
  }
}

// ── Create form (menu and protected-main dialog) ──────────────────────────────

const CreateBranchForm: React.FC<{ onCancel?: () => void; autoFocus?: boolean }> = ({ onCancel, autoFocus }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('feat/');
  const [mode, setMode] = useState<ForkMode>('none');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const nm = name.trim();
    if (!nm || nm === 'feat/') { setErr(t('release.switcher.nameRequired', 'Escribe un nombre para la rama.')); return; }
    setBusy(true); setErr(null);
    try {
      const b = await createAndWait(nm, mode);
      switchBranch(toActive(b));
    } catch (ex) {
      setErr(errText(ex));
      setBusy(false);
    }
  };

  return (
    <form className="branch-form" onSubmit={submit} aria-label={t('release.switcher.createTitle', 'Nueva rama')}>
      <label htmlFor="branch-new-name">{t('release.switcher.nameLabel', 'Nombre de la rama')}</label>
      <input id="branch-new-name" value={name} autoFocus={autoFocus} disabled={busy}
        onChange={(e) => setName(e.target.value)} placeholder="feat/mi-cambio" />
      <label htmlFor="branch-new-data">{t('release.switcher.dataLabel', 'Datos')}</label>
      <select id="branch-new-data" value={mode} disabled={busy} onChange={(e) => setMode(e.target.value as ForkMode)}>
        <option value="none">{t('release.switcher.dataNone', 'Solo configuración')}</option>
        <option value="all">{t('release.switcher.dataAll', 'Configuración y registros')}</option>
      </select>
      {busy && <div className="branch-form-note">{t('release.switcher.creating', 'Creando la rama: copiando la configuración de main…')}</div>}
      {err && <div className="branch-form-err" role="alert">{err}</div>}
      <div className="branch-form-row">
        {onCancel && <Button size="sm" variant="ghost" type="button" onClick={onCancel} disabled={busy}>{t('release.switcher.cancel', 'Cancelar')}</Button>}
        <Button size="sm" variant="primary" type="submit" loading={busy} icon={<GitBranch size={13} />}>
          {t('release.switcher.createAndSwitch', 'Crear y cambiar')}
        </Button>
      </div>
    </form>
  );
};

// ── Switcher (top bar, next to the project picker) ────────────────────────────

export const BranchSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const active = useActiveBranch();
  const { branches, main, fetchBranches } = useReleaseStore();
  const setRequestOpen = useBranchUi((s) => s.setRequestOpen);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { if (open) void fetchBranches(); }, [open, fetchBranches]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setCreating(false); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = (b: Branch | null) => {
    setOpen(false);
    if ((b?.name ?? null) === (active?.name ?? null)) return;
    switchBranch(b ? toActive(b) : null);
  };

  return (
    <div className="branch-switcher" ref={ref}>
      <label id="branch-switcher-label">{t('release.switcher.label', 'Rama')}</label>
      <button type="button" className="branch-pill" data-branch={active ? 'true' : 'false'}
        aria-haspopup="menu" aria-expanded={open} aria-labelledby="branch-switcher-label"
        data-testid="branch-switcher" onClick={() => setOpen((o) => !o)}>
        <GitBranch size={13} />
        <span>{active ? active.name : 'main'}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="branch-menu" role="menu">
          <div className="branch-menu-title">{t('release.switcher.title', 'Cambiar de rama')}</div>
          <button type="button" role="menuitemradio" aria-checked={!active} className="branch-option" onClick={() => choose(null)}>
            <GitBranch size={13} /> main
            <small>{main?.protected ? <><Lock size={10} /> {t('release.protected', 'protegida')}</> : t('release.switcher.mainCaption', 'lo que opera el tenant')}</small>
          </button>
          {branches.map((b) => (
            <button key={b.tenant_id} type="button" role="menuitemradio" aria-checked={active?.name === b.name}
              className="branch-option" disabled={!LIVE.has(b.status)} onClick={() => choose(b)}>
              <GitBranch size={13} /> {b.name}
              <small>{t(`release.branchStatus.${b.status}`, b.status)}</small>
            </button>
          ))}
          <hr />
          {active && (
            <button type="button" role="menuitem" className="branch-option" onClick={() => { setOpen(false); setRequestOpen(true); }}>
              <GitPullRequest size={13} /> {t('release.switcher.requestChange', 'Solicitar cambio a main')}
            </button>
          )}
          {creating
            ? <CreateBranchForm autoFocus onCancel={() => setCreating(false)} />
            : (
              <button type="button" role="menuitem" className="branch-option" onClick={() => setCreating(true)}>
                <Plus size={13} /> {t('release.switcher.newBranch', 'Nueva rama')}
              </button>
            )}
        </div>
      )}
    </div>
  );
};

// ── Permanent indicator ───────────────────────────────────────────────────────

export const BranchBanner: React.FC = () => {
  const { t } = useTranslation();
  const active = useActiveBranch();
  const setRequestOpen = useBranchUi((s) => s.setRequestOpen);
  const activeName = active?.name;
  // A branch merged, deleted or expired elsewhere: go back to main instead of
  // leaving every request to fail with a tenant mismatch.
  useEffect(() => {
    if (!activeName) return;
    let cancelled = false;
    void (async () => {
      await useReleaseStore.getState().fetchBranches();
      const { branches, error } = useReleaseStore.getState();
      if (cancelled || error) return;
      const b = branches.find((x) => x.name === activeName);
      if (!b || !LIVE.has(b.status)) switchBranch(null);
    })();
    return () => { cancelled = true; };
  }, [activeName]);
  const uiV2 = useUiV2();
  if (!active) return null;
  if (uiV2) {
    const vs = useReleaseStore.getState().branches.find((b) => b.name === active.name)?.vs_main;
    const n = vs ? vs.added + vs.modified + vs.deleted : null;
    return (
      <div className="branch-taskbar" role="status" data-testid="branch-banner">
        <GitBranch size={13} />
        <span className="branch-taskbar__text">
          {t('release.switcher.taskbarIn', 'Estás en rama')} <b>{active.name}</b>
          {n !== null && <> · {t('release.switcher.taskbarChanges', '{{n}} cambios', { n })}</>}
        </span>
        <Button size="sm" variant="primary" icon={<GitPullRequest size={13} />} onClick={() => setRequestOpen(true)}>
          {t('release.switcher.requestApproval', 'Solicitar aprobación')}
        </Button>
        <Button size="sm" variant="ghost" icon={<Undo2 size={13} />} onClick={() => switchBranch(null)}>
          {t('release.switcher.exitBranch', 'Salir')}
        </Button>
      </div>
    );
  }
  return (
    <div className="branch-banner" role="status" data-testid="branch-banner">
      <span className="branch-banner-tag"><GitBranch size={11} /> {t('release.switcher.bannerTag', 'Rama')}</span>
      <span className="branch-banner-text">
        {t('release.switcher.bannerPrefix', 'Estás trabajando en la rama')} <b>{active.name}</b>.{' '}
        {t('release.switcher.bannerBody', 'Lo que edites aquí no cambia main hasta que se apruebe una solicitud de cambio.')}
      </span>
      <Button size="sm" variant="secondary" icon={<GitPullRequest size={13} />} onClick={() => setRequestOpen(true)}>
        {t('release.switcher.requestChange', 'Solicitar cambio a main')}
      </Button>
      <Button size="sm" variant="ghost" icon={<Undo2 size={13} />} onClick={() => switchBranch(null)}>
        {t('release.switcher.backToMain', 'Volver a main')}
      </Button>
    </div>
  );
};

// ── Dialogs ───────────────────────────────────────────────────────────────────

const Dialog: React.FC<{ title: string; icon: React.ReactNode; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; testId?: string }> = ({ title, icon, onClose, children, footer, testId }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="branch-dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="branch-dialog" role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
        <header>{icon}{title}</header>
        <div className="branch-dialog-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
};

export const RequestChangeDialog: React.FC = () => {
  const { t } = useTranslation();
  const active = useActiveBranch();
  const { requestOpen, setRequestOpen } = useBranchUi();
  const { requestApproval, setPendingOpenId } = useReleaseStore();
  const navigateTo = useNavigationStore((s) => s.navigateTo);
  const [title, setTitle] = useState('');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [opened, setOpened] = useState<{ id: string; number: number } | null>(null);
  const close = useCallback(() => { setRequestOpen(false); setErr(null); setOpened(null); }, [setRequestOpen]);
  if (!requestOpen || !active) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setErr(t('release.switcher.titleRequired', 'Escribe un título para la solicitud.')); return; }
    setBusy(true); setErr(null);
    try {
      const cr = await requestApproval(active.name, title.trim(), rationale.trim());
      setOpened({ id: cr.id, number: cr.number });
      setTitle(''); setRationale('');
    } catch (ex) {
      setErr(errText(ex));
    } finally { setBusy(false); }
  };

  const view = () => { if (opened) setPendingOpenId(opened.id); close(); navigateTo('changes'); };

  return (
    <Dialog title={t('release.switcher.requestTitle', 'Solicitar cambio a main')} icon={<GitPullRequest size={16} />} onClose={close} testId="request-change-dialog"
      footer={opened
        ? <><Button size="sm" variant="ghost" onClick={close}>{t('release.switcher.close', 'Cerrar')}</Button><Button size="sm" variant="primary" onClick={view}>{t('release.switcher.viewRequest', 'Ver solicitud')}</Button></>
        : undefined}>
      {opened ? (
        <div className="branch-dialog-ok" role="status">
          {t('release.switcher.requestOpened', 'Abriste la solicitud #{{n}}. Un admin del tenant la revisa y la fusiona en main; mientras está en revisión, la rama queda en solo lectura.', { n: opened.number })}
        </div>
      ) : (
        <form className="branch-form" onSubmit={submit}>
          <div className="branch-form-note">
            {t('release.switcher.requestIntro', 'Los cambios de la rama {{name}} pasan a main cuando un admin aprueba la solicitud.', { name: active.name })}
          </div>
          <label htmlFor="cr-title">{t('release.switcher.requestTitleLabel', 'Título')}</label>
          <input id="cr-title" value={title} autoFocus disabled={busy} onChange={(e) => setTitle(e.target.value)} />
          <label htmlFor="cr-rationale">{t('release.switcher.requestRationale', 'Qué cambia y cómo lo probaste')}</label>
          <textarea id="cr-rationale" value={rationale} disabled={busy} onChange={(e) => setRationale(e.target.value)} />
          {err && <div className="branch-form-err" role="alert">{err}</div>}
          <div className="branch-form-row">
            <Button size="sm" variant="ghost" type="button" onClick={close} disabled={busy}>{t('release.switcher.cancel', 'Cancelar')}</Button>
            <Button size="sm" variant="primary" type="submit" loading={busy} icon={<GitPullRequest size={13} />}>{t('release.switcher.requestSubmit', 'Abrir solicitud')}</Button>
          </div>
        </form>
      )}
    </Dialog>
  );
};

/** Explains a write refused by protected main and offers a branch. */
export const ProtectedMainDialog: React.FC = () => {
  const { t } = useTranslation();
  const { protectedMessage, setProtectedMessage } = useBranchUi();
  const { branches, fetchBranches } = useReleaseStore();
  const close = useCallback(() => setProtectedMessage(null), [setProtectedMessage]);

  useEffect(() => {
    const onRefusal = (e: Event) => {
      const message = (e as CustomEvent<{ message?: string }>).detail?.message;
      setProtectedMessage(message || t('release.switcher.protectedFallback', 'main está protegida: para editar, crea una rama o cambia a una.'));
    };
    window.addEventListener(PROTECTED_MAIN_EVENT, onRefusal);
    return () => window.removeEventListener(PROTECTED_MAIN_EVENT, onRefusal);
  }, [setProtectedMessage, t]);
  useEffect(() => { if (protectedMessage) void fetchBranches(); }, [protectedMessage, fetchBranches]);

  if (!protectedMessage) return null;
  const live = branches.filter((b) => b.status === 'active');
  return (
    <Dialog title={t('release.switcher.protectedTitle', 'main está protegida')} icon={<ShieldAlert size={16} />} onClose={close} testId="protected-main-dialog">
      <div>{protectedMessage}</div>
      <div className="branch-form-note">
        {t('release.switcher.protectedWhy', 'Un admin protegió main: la configuración cambia solo con una solicitud de cambio aprobada. Trabaja en una rama, pruébala y abre la solicitud desde la barra. Lo que no guardaste en esta pantalla no pasa a la rama.')}
      </div>
      {live.length > 0 && (
        <div className="branch-form">
          <label>{t('release.switcher.protectedExisting', 'Cambia a una rama tuya')}</label>
          {live.map((b) => (
            <button key={b.tenant_id} type="button" className="branch-option" onClick={() => switchBranch(toActive(b))}>
              <GitBranch size={13} /> {b.name}
            </button>
          ))}
        </div>
      )}
      <div className="branch-menu-title" style={{ padding: '4px 0 0' }}>{t('release.switcher.createTitle', 'Nueva rama')}</div>
      <CreateBranchForm autoFocus onCancel={close} />
    </Dialog>
  );
};

/** Everything the shell mounts once: banner + the two dialogs. */
export const BranchShell: React.FC = () => (
  <>
    <BranchBanner />
    <RequestChangeDialog />
    <ProtectedMainDialog />
  </>
);
